import React from 'react';

import { di } from 'react-magnetic-di';

import { Stack } from '@atlaskit/primitives/compiled';

import { FormRenderMode } from '../../../../../../common/types/render-mode';
import {
	useFormField,
	useFormRendererState,
	useFormRenderMode,
} from '../../../../../../controllers/form-renderer-store';
import { defaultFormFieldComponentRenderers } from './constants';
import { FormFieldErrorRenderer } from './error-renderer';
import type { FormFieldRendererProps } from './types';
import { UnknownField } from './unknown-field';
import { getFormFieldComponentRenderer } from './utils';

export const FormFieldRenderer = ({ fieldId }: FormFieldRendererProps): React.JSX.Element => {
	di(useFormField, useFormRendererState, useFormRenderMode);
	const { formId, renderers } = useFormRendererState();
	const renderMode = useFormRenderMode();
	const formField = useFormField({ fieldId });
	const customFormFieldComponentRenderers = renderers.formFieldComponentRenderers || {};

	if (formField === undefined) {
		return (
			<UnknownField
				fieldId={fieldId}
				fieldInputId="pf-unknown-fieldId"
				fieldLabelId="pf-unknown-fieldId-label"
				fieldErrorId="pf-unknown-fieldId-error"
			/>
		);
	}

	const fieldInputId = `pf-${formId}-${formField.type}-${fieldId}`;
	const fieldLabelId = `${fieldInputId}-label`;
	const fieldErrorId = `${fieldInputId}-error`;

	const FormFieldContainerRenderer = getFormFieldComponentRenderer(
		formField.type,
		customFormFieldComponentRenderers.Container,
		defaultFormFieldComponentRenderers.Container,
	);
	const FormFieldHelperMessageRenderer = getFormFieldComponentRenderer(
		formField.type,
		customFormFieldComponentRenderers.HelperMessage,
		defaultFormFieldComponentRenderers.HelperMessage,
	);
	const FormFieldLabelRenderer = getFormFieldComponentRenderer(
		formField.type,
		customFormFieldComponentRenderers.Label,
		defaultFormFieldComponentRenderers.Label,
	);
	const FormFieldInputRenderer = getFormFieldComponentRenderer(
		formField.type,
		customFormFieldComponentRenderers.Input,
		defaultFormFieldComponentRenderers.Input,
	);

	return (
		<FormFieldContainerRenderer
			fieldId={fieldId}
			fieldInputId={fieldInputId}
			fieldLabelId={fieldLabelId}
			fieldErrorId={fieldErrorId}
		>
			<Stack>
				<FormFieldLabelRenderer
					fieldId={fieldId}
					fieldInputId={fieldInputId}
					fieldLabelId={fieldLabelId}
					fieldErrorId={fieldErrorId}
				/>
				<FormFieldHelperMessageRenderer
					fieldId={fieldId}
					fieldInputId={fieldInputId}
					fieldLabelId={fieldLabelId}
					fieldErrorId={fieldErrorId}
				/>
				<FormFieldInputRenderer
					fieldId={fieldId}
					fieldInputId={fieldInputId}
					fieldLabelId={fieldLabelId}
					fieldErrorId={fieldErrorId}
				/>
				{renderMode === FormRenderMode.EDIT && (
					<FormFieldErrorRenderer
						fieldId={fieldId}
						fieldInputId={fieldInputId}
						fieldLabelId={fieldLabelId}
						fieldErrorId={fieldErrorId}
					/>
				)}
			</Stack>
		</FormFieldContainerRenderer>
	);
};
